/**
 * Issuing a receipt number on the till.
 *
 * Specification: ../test/receipt-numbers.spec.ts. Master plan §2 D7, §4 M7, §8 P6.
 *
 * THE RULE THAT DECIDES THE IMPLEMENTATION, and it is two lines of ordering:
 *
 *     `nextNumber` IS ADVANCED AND PERSISTED **BEFORE** THE EVENT IS EMITTED.
 *
 * A crash in that window LOSES one number — a gap, which the server detects,
 * attributes to this lease and this device, and reports. You can explain a gap.
 *
 * The other order REISSUES one: emit, then persist, crash between, and the next
 * check takes the same number. Two fiscal documents with one identity, and not
 * repairable afterwards because one of them is in a guest's pocket.
 *
 *     WHEN IN DOUBT, BURN A NUMBER.
 *
 * So `issue()` returns a number that is ALREADY SPENT. A caller that fails to
 * emit has burnt it, on purpose.
 *
 * WHAT EXHAUSTION MAY STOP. Closing a check, and nothing else. Opening a table,
 * ringing an item and firing to the kitchen all continue — a seated guest must
 * be served and the kitchen must get the food. A receipt with no number is not
 * a receipt; a kitchen ticket is not a receipt either.
 */

import { formatDocumentNumber } from "@gf/kernel/numbering/format";
import type { HeldBlock, SqliteReceiptLeaseStore } from "./receipt-lease-store";

/** What `receipt.issued` carries, minus the check id the caller knows. */
export interface IssuedNumber {
  readonly leaseId: string;
  readonly seriesKey: string;
  readonly period: string;
  readonly number: number;
  readonly receiptNumber: string;
}

/** The server, as this module needs it. Implemented by `numbering-client.ts`. */
export interface LeaseSource {
  lease(args: { seriesKey: string; size: number }): Promise<{
    leaseId: string;
    seriesKey: string;
    period: string;
    fromNumber: number;
    toNumber: number;
    expiresAt: Date;
  }>;
  returnLease(leaseId: string): Promise<void>;
}

export interface ReceiptNumbersDeps {
  readonly store: SqliteReceiptLeaseStore;
  readonly source: LeaseSource;
  /** The venue's current period. Defaults to the calendar year of `now`. */
  readonly period?: () => string;
  readonly seriesKey?: string;
  readonly blockSize?: number;
}

/** Ask for more at 20 % left. Low enough to be rare, high enough to survive a rush. */
const LOW_WATER = 0.2;

export class ReceiptNumbers {
  readonly #deps: ReceiptNumbersDeps;
  readonly #seriesKey: string;
  readonly #blockSize: number;
  /**
   * Mirrors the durable rows, so `remaining()` is synchronous for the banner.
   *
   * A LIST PER PERIOD, not a single block. A top-up is held ALONGSIDE the block
   * it tops up and spent after it, so the tail of the current block is never
   * burnt. The first version replaced the block instead, which left a gap of up
   * to 20 % of every block the till ever held — the spec caught it going
   * straight from 3 to 4.
   */
  #held = new Map<string, HeldBlock[]>();

  constructor(deps: ReceiptNumbersDeps) {
    this.#deps = deps;
    this.#seriesKey = deps.seriesKey ?? "receipt";
    this.#blockSize = deps.blockSize ?? 200;
  }

  #period(): string {
    return this.#deps.period ? this.#deps.period() : String(new Date().getFullYear());
  }

  #blocksFor(period: string): HeldBlock[] {
    return this.#held.get(`${this.#seriesKey} ${period}`) ?? [];
  }

  async #reload(): Promise<void> {
    this.#held.clear();
    for (const block of await this.#deps.store.all()) {
      const key = `${block.seriesKey} ${block.period}`;
      this.#held.set(key, [...(this.#held.get(key) ?? []), block]);
    }
  }

  /**
   * Load what is already held, and lease only if this period has nothing left.
   *
   * RESUMING RATHER THAN RE-LEASING is the whole reason a block is persisted. A
   * till that leased on every start would leave a block's worth of gaps every
   * time somebody refreshed the tab.
   */
  async ensureBlock(): Promise<void> {
    await this.#reload();
    if (this.remaining() > 0) return;
    await this.#leaseInto();
  }

  /**
   * Pre-lease the NEXT period, called within 7 days of the boundary.
   *
   * The mitigation for the one night a year the scheme is awkward: a till
   * holding 2026 numbers cannot spend them in 2027, and leasing is an online
   * step at the worst possible moment. Holding both across the boundary means
   * midnight needs no network.
   *
   * The residual risk is real and written down rather than hidden: a till that
   * is offline across New Year's Eve AND was never online in the preceding week
   * cannot close a check.
   */
  async preLease(period: string): Promise<void> {
    if (this.#blocksFor(period).length > 0) return;
    await this.#leaseInto();
  }

  async #leaseInto(): Promise<void> {
    const leased = await this.#deps.source.lease({
      seriesKey: this.#seriesKey,
      size: this.#blockSize,
    });

    /**
     * THE SERVER'S PERIOD IS THE ONE IT IS FILED UNDER.
     *
     * `period` is derived server-side from the venue's business date, and the
     * till's own idea of the date is precisely what cannot be trusted. Filing
     * the block under the period we ASKED for rather than the one we got would
     * have the till spend a 2026 block believing it was 2027 — refused by the
     * server after the receipt was already printed.
     */
    const block: HeldBlock = {
      leaseId: leased.leaseId,
      seriesKey: leased.seriesKey,
      period: leased.period,
      fromNumber: leased.fromNumber,
      toNumber: leased.toNumber,
      nextNumber: leased.fromNumber,
      expiresAt: leased.expiresAt,
    };

    await this.#deps.store.put(block);

    const key = `${block.seriesKey} ${block.period}`;
    this.#held.set(
      key,
      [...this.#held.get(key)?.filter((b) => b.leaseId !== block.leaseId) ?? [], block].sort(
        (a, b) => a.fromNumber - b.fromNumber,
      ),
    );
  }

  /**
   * Take the next number for this check.
   *
   * Returns null when there is nothing left, rather than throwing or inventing
   * one: the caller has to be able to refuse the CLOSE specifically and tell
   * the server why, while everything else on the till keeps working.
   */
  async issue(_checkId: string): Promise<IssuedNumber | null> {
    const period = this.#period();
    if (this.#blocksFor(period).length === 0) return null;

    // DURABLE FIRST. Everything after this line is allowed to fail, and if it
    // does the number is burnt rather than reissued.
    const taken = await this.#deps.store.take(this.#seriesKey, period);
    if (!taken) return null;

    const block = this.#blocksFor(period).find((b) => b.leaseId === taken.leaseId);
    if (!block) return null;

    this.#held.set(
      `${this.#seriesKey} ${period}`,
      this.#blocksFor(period).map((b) =>
        b.leaseId === taken.leaseId ? { ...b, nextNumber: taken.number + 1 } : b,
      ),
    );

    return {
      leaseId: block.leaseId,
      seriesKey: block.seriesKey,
      period: block.period,
      number: taken.number,
      receiptNumber: formatDocumentNumber({ period: block.period, number: taken.number }),
    };
  }

  /**
   * How many are left for the CURRENT period, across every block held.
   *
   * Synchronous, because it feeds the status banner on every render and a round
   * trip per frame is not a banner.
   *
   * Zero when nothing is held, which is the honest answer: an un-leased till
   * cannot close a check. `Number.MAX_SAFE_INTEGER` was the placeholder here,
   * and it told `sellState` the till could always sell.
   */
  remaining(): number {
    return this.#blocksFor(this.#period()).reduce(
      (total, block) => total + Math.max(0, block.toNumber - block.nextNumber + 1),
      0,
    );
  }

  /**
   * Ask for another block at the 20 % mark.
   *
   * SWALLOWS ITS OWN FAILURE. The normal reason a top-up fails is that the
   * network is down — which is exactly when the till still holds numbers and
   * must keep trading. Throwing would take a till that can still close forty
   * checks and stop it closing any.
   *
   * The current block is NOT returned here. It still has numbers in it, and
   * handing back a lease the till is still spending would turn every one of
   * them into a gap. Leases go back at drawer close, when the till is genuinely
   * finished with them.
   */
  async topUpIfLow(): Promise<void> {
    const period = this.#period();
    const blocks = this.#blocksFor(period);
    if (blocks.length === 0) return;

    const capacity = blocks.reduce((n, b) => n + (b.toNumber - b.fromNumber + 1), 0);
    if (this.remaining() > Math.ceil(capacity * LOW_WATER)) return;

    try {
      await this.#leaseInto();
    } catch {
      // Still holding what we had. Nothing has changed.
    }
  }

  /** Every block held, so drawer close can hand them all back. */
  async held(): Promise<HeldBlock[]> {
    return this.#deps.store.all();
  }

  /**
   * Hand every held block back, at drawer close.
   *
   * This is the whole difference between "a till that accounted for its block"
   * and "a till nobody has heard from": a returned lease's unused tail is an
   * explained gap, an unreturned one is a device somebody has to go and find.
   */
  async returnAll(): Promise<void> {
    for (const block of await this.#deps.store.all()) {
      try {
        await this.#deps.source.returnLease(block.leaseId);
        await this.#deps.store.forget(block.leaseId);
      } catch {
        // Offline at close. The lease expires on its own and the gap is
        // reported as LEASE_EXPIRED instead of LEASE_RETURNED_UNUSED — less
        // tidy, equally explainable, and not worth blocking a close over.
      }
    }
    await this.#reload();
  }
}
